import { navigateTo } from "@/lib/navigation-service";
import { fashionPaths } from "@/modules";
import { useProductDetailModalStore } from "@/features/product-detail/store/product-detail-modal-store";
import { useStreamingStore } from "./store/streaming-store";
import { exploreRedirectTarget } from "./explore-redirect";
import {
  cartPageNavigation,
  resolveRedirection,
  type StreamNavigation,
} from "./stream-navigation";

export {
  cartPageNavigation,
  resolveRedirection,
  type StreamNavigation,
} from "./stream-navigation";

const REDIRECT_DELAY_MS = 2000;

type RedirectPhase = "pending" | "announced";

interface PendingRedirect {
  phase: RedirectPhase;
  navigation: StreamNavigation;
  chatKey: string;
  streamId: string;
  timer: ReturnType<typeof setTimeout> | null;
}

let pending: PendingRedirect | null = null;

const isActiveChat = (chatKey: string): boolean =>
  window.location.pathname === `/chat/${chatKey}`;

const sameNavigation = (a: StreamNavigation, b: StreamNavigation): boolean => {
  if (a.kind !== b.kind) return false;
  if (a.kind === "route" && b.kind === "route") return a.url === b.url;
  if (a.kind === "product" && b.kind === "product") {
    return a.productId === b.productId && a.offerId === b.offerId;
  }
  return false;
};

/**
 * Explore, teslim edilen sonuç grubunu Discover'a taşır. Grup yoksa düz
 * vitrin rotası kalır. Diğer destination'lar saf çözümleyicidedir.
 */
export const resolveStreamRedirection = (
  content: unknown,
): StreamNavigation | null => {
  if (content && typeof content === "object") {
    const raw = content as { destination?: unknown; exploreContext?: unknown };
    if (raw.destination === "explore") {
      const explore = exploreRedirectTarget(
        raw.exploreContext as Parameters<typeof exploreRedirectTarget>[0],
      );
      if (explore) {
        return { kind: "route", url: explore.url, state: explore.state };
      }
    }
  }

  return resolveRedirection(content, { shopPath: fashionPaths.shop() });
};

const clearPending = (): void => {
  if (pending?.timer) clearTimeout(pending.timer);
  pending = null;
};

const ownsStreamingTurn = (chatKey: string, streamId: string): boolean => {
  if (!isActiveChat(chatKey)) return false;
  const session = useStreamingStore.getState().getSession(chatKey);
  return session.streamId === streamId && session.status === "streaming";
};

const queueNavigation = (
  navigation: StreamNavigation,
  chatKey: string,
  streamId: string,
  replace: boolean,
): boolean => {
  if (!ownsStreamingTurn(chatKey, streamId)) return false;

  if (
    pending?.chatKey === chatKey &&
    pending.streamId === streamId &&
    sameNavigation(pending.navigation, navigation)
  ) {
    return true;
  }

  if (!replace && pending) return false;

  clearPending();
  pending = {
    phase: "pending",
    navigation,
    chatKey,
    streamId,
    timer: null,
  };
  return true;
};

/**
 * Redirect event'ini kaydeder; navigasyon yapmaz.
 *
 * Shopping Agent bu event'i son metin token'ından önce gönderir. Yönlendirme
 * ancak gerçek stream terminali geldiğinde `flushRedirect` ile uygulanır.
 * Yeni bir redirection, aynı turda daha önce kuyruğa alınmış sepet
 * yönlendirmesinin yerini alır.
 */
export const requestNavigation = (
  navigation: StreamNavigation,
  chatKey: string,
  streamId: string,
): boolean => queueNavigation(navigation, chatKey, streamId, true);

/**
 * Sepet sayfasını açmayı ister. Aynı turda zaten bir redirection kuyrukta
 * ise ona dokunmaz: checkout gibi açık bir yönlendirme, sepet snapshot'ından
 * önce gelir.
 */
export const requestCartNavigation = (
  chatKey: string,
  streamId: string,
): boolean => queueNavigation(cartPageNavigation(), chatKey, streamId, false);

export type FlushedNavigation = "route" | "overlay";

/** Gerçek başarı terminalinden sonra bekleyen etkiyi uygular. */
export const flushRedirect = (
  chatKey: string,
  streamId: string,
): FlushedNavigation | null => {
  if (
    !pending ||
    pending.phase !== "pending" ||
    pending.chatKey !== chatKey ||
    pending.streamId !== streamId
  ) {
    return null;
  }

  const session = useStreamingStore.getState().getSession(chatKey);
  if (
    !isActiveChat(chatKey) ||
    session.streamId !== streamId ||
    session.status !== "completed"
  ) {
    clearPending();
    return null;
  }

  const redirect = pending;

  if (redirect.navigation.kind === "product") {
    clearPending();
    useProductDetailModalStore
      .getState()
      .openModal(redirect.navigation.productId, redirect.navigation.offerId);
    return "overlay";
  }

  redirect.phase = "announced";
  redirect.timer = setTimeout(() => {
    if (pending !== redirect) return;

    const latestSession = useStreamingStore.getState().getSession(chatKey);
    clearPending();
    if (
      !isActiveChat(chatKey) ||
      latestSession.streamId !== streamId ||
      latestSession.status !== "completed"
    ) {
      return;
    }

    const route = redirect.navigation;
    if (route.kind !== "route") return;
    navigateTo(route.url, route.state);
  }, REDIRECT_DELAY_MS);

  return "route";
};

/** Yalnız verilen sohbet/tur sahibi bekleyen redirect'i iptal edebilir. */
export const cancelRedirect = (chatKey?: string, streamId?: string): void => {
  if (!pending) return;
  if (chatKey !== undefined && pending.chatKey !== chatKey) return;
  if (streamId !== undefined && pending.streamId !== streamId) return;
  clearPending();
};

/** Test/teşhis için: şu anda bekleyen bir yönlendirme var mı? */
export const hasPendingRedirect = (chatKey?: string): boolean => {
  if (!pending) return false;
  return chatKey === undefined || pending.chatKey === chatKey;
};
